import { isEditableEventTarget } from '@littlebird/action-cards';

import type { KeyName } from '@/lib/key';

export type FeedShortcutActions = {
  toggle?: () => void;
  // Puts the caret in the object's first field; only an open card has one
  edit?: () => void;
  chat?: () => void;
  dismiss?: () => void;
  // Checks the card off: marks a to-do or a draft done, or an update read
  markDone?: () => void;
  runPrimary?: () => void;
  pin?: () => void;
  snooze?: () => void;
};

// `?` is Shift and slash, which the shared key names have no single name for
export type FeedGuideKey = KeyName | '?';

// The keys the Feed and Home answer, as the shortcut list that ? opens shows them. Each entry's `keys` holds its
// alternatives; the keys within one alternative are pressed together
export const FEED_KEY_GUIDE: readonly {
  label: string;
  keys: readonly (readonly FeedGuideKey[])[];
}[] = [
  {
    label: 'Next or previous card',
    keys: [
      ['arrowdown', 'arrowup'],
      ['j', 'k'],
    ],
  },
  { label: 'Open or close the card', keys: [['enter']] },
  { label: 'Close the card', keys: [['escape']] },
  { label: 'Mark done, or mark an update read', keys: [['d']] },
  { label: 'Dismiss', keys: [['backspace']] },
  { label: 'Snooze', keys: [['s']] },
  { label: 'Pin or unpin', keys: [['p']] },
  { label: 'Chat about the card', keys: [['c']] },
  { label: "Edit the card's first field", keys: [['e']] },
  { label: "Run the card's main action", keys: [['mod', 'enter']] },
  { label: 'Show these shortcuts', keys: [['?']] },
];

// Typing in a field, a menu, a select's options or a dialog keeps its keys from the Feed's
export const ownsFeedKeys = (target: EventTarget | null): boolean =>
  isEditableEventTarget(target) ||
  (target instanceof Element &&
    target.closest(
      '[role="dialog"], [role="menu"], [role="listbox"], [data-feed-own-keys]'
    ) !== null);

type ShortcutEvent = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'target'
> & { preventDefault: () => void; stopPropagation: () => void };

const actionFor = (
  event: ShortcutEvent,
  actions: FeedShortcutActions,
  card: Element | null | undefined
): (() => void) | undefined => {
  const key = event.key.toLowerCase();
  // Only the primary action needs ⌘ (Ctrl off macOS): it sends or changes something in another app, which a stray key
  // must not do. Every other action is one key, and typing in a field keeps its keys
  if (event.metaKey || event.ctrlKey)
    return key === 'enter' && !event.shiftKey ? actions.runPrimary : undefined;
  if (event.shiftKey) return undefined;
  if (key === 'e') return actions.edit;
  if (key === 'c') return actions.chat;
  if (key === 'd') return actions.markDone;
  if (key === 's') return actions.snooze;
  if (key === 'p') return actions.pin;
  if (key === 'backspace' || key === 'delete') return actions.dismiss;
  // A focused button or link in the card handles its own Enter and Space; one elsewhere on the page doesn't
  const ownsActivation =
    event.target instanceof Element &&
    event.target.closest('button, a') !== null &&
    (!card || card.contains(event.target));
  if ((key === 'enter' || key === ' ') && !ownsActivation)
    return actions.toggle;
  return undefined;
};

// Enter, E, C, D, S, P, ⌫ and ⌘↵ on the focused or open feed row; typing in a field or a menu keeps its keys. `card`
// is the open card, for keys pressed anywhere on the page
export function handleFeedShortcut(
  event: ShortcutEvent,
  actions: FeedShortcutActions,
  card?: Element | null
): boolean {
  if (event.altKey) return false;
  if (ownsFeedKeys(event.target)) return false;
  const action = actionFor(event, actions, card);
  if (!action) return false;
  event.preventDefault();
  // A card's keys stop with the card; Enter and Space still reach the element that has focus
  const toggles = action === actions.toggle;
  if (!toggles) event.stopPropagation();
  action();
  return true;
}

// A title at rest is a button that becomes its field once focused
const FIRST_FIELD =
  'textarea:not([disabled]), input:not([type="hidden"]):not([type="checkbox"]):not([disabled]), [contenteditable="true"], [data-field-at-rest]';

export const focusFirstField = (frame: HTMLElement | null): void => {
  frame?.querySelector<HTMLElement>(FIRST_FIELD)?.focus();
};
